import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  translatePage,
  useTranslation,
} from '@ikea-customer-self-serve/gatsby-plugin-translations';
import variables from '@ingka/variables/design-tokens';
import cssIngka from '@ingka/variables/colours-css';
import { withPrefix } from 'gatsby';
import MobileCart from '../components/MobileCart';
import { useMedia } from '../hooks/useMedia';
import SiteMetadata from '../components/SiteMetadata';
import Search from '../components/Search';
import InCart from '../components/InCart';
import Breadcrumbs from '../components/Breadcrumbs';
import ProductParts from '../components/ProductParts/ProductParts';
import { sparePartService } from '../services/SparePartService';
import { SparePart } from '../interfaces/Item';
import { getUniqueValuesFromString, getURLParameters } from '../helpers/url';
import TMarkUp from '../components/TMarkUp';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import SSRIcon from '@ingka/ssr-icon';
import SearchLandingPage from '../components/SearchLandingPage';
import Loading, { LoadingBall } from '@ingka/loading';
import MarketUnavailablePage from './market-unavailable';

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const OrderLayout = styled.div`
  display: flex;
  flex-direction: column;
  ${`@media (min-width: ${variables.breakpointM}) {
      flex-direction: row;
      justify-content: space-between;
      margin-bottom: 50px;
  }`}
`;

const FlexContent = styled.div`
  display: flex;
  flex-direction: column;
  ${`@media (min-width: ${variables.breakpointM}) {
      margin-bottom: 50px;
  }`}
`;

const StyledDivider = styled.hr`
  max-width: 550px;
  margin: 0px;
  height: 0px;
  border-block-start: 1px solid ${cssIngka.colourNeutral3};
`;

const SearchLayoutTitle = styled.h1.attrs({ as: 'h1', className: 'h1' })`
  margin: 16px 0;
  ${`@media (min-width: ${variables.breakpointM}) {
      margin-top: 60px;
      margin-bottom: 30px;
  }`}
`;

const InformationWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 18px;
  margin-bottom: 24px;
`;

const IconWrapper = styled.div`
  color: ${cssIngka.colourStaticIkeaBrandBlue};
  margin-right: 8px;
`;

const ArticleIdNotFound = styled.div`
  margin-top: 32px;
  margin-bottom: 60px;
  color: ${cssIngka.colourSemanticNegative};
`;

const Order = (): JSX.Element => {
  // Check market availability BEFORE calling any hooks to comply with Rules of Hooks
  const isMarketUnavailable =
    typeof CSS_CONFIG !== 'undefined' &&
    typeof CSS_CONFIG.ISMARKETDISABLED != 'undefined' &&
    CSS_CONFIG.ISMARKETDISABLED === true;

  if (isMarketUnavailable) {
    return <MarketUnavailablePage />;
  }

  const { t } = useTranslation();
  const crumbs = [{ url: withPrefix('/'), text: t('seoContent.page.search') }];
  const title = t('seoContent.page.order');
  const { isDesktop } = useMedia();
  const [spareparts, setSpareparts] = useState<SparePart[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // ArtIds are used by redirects from Purchase History
  const urlParamArticle = getURLParameters('articleIds');
  const artIds = urlParamArticle
    ? getUniqueValuesFromString(urlParamArticle)
    : '';
  const hasArticleIdsQueryInUrl = artIds ? true : false;

  useEffect(() => {
    const fetchPartsUsingArticleIds = async () => {
      if (artIds) {
        setIsLoading(true);
        let result: SparePart[] = [];

        try {
          // Truncate artIds when it exceeds upper limit
          const tempArtIds = artIds.slice(0, CSS_CONFIG.UPPERLIMITARTICLES);
          result = await sparePartService.fetchSparePartRelatedToArticleId(
            tempArtIds.join(',')
          );
        } catch (error) {
          result = [];
        } finally {
          setIsLoading(false);
          setSpareparts(result);
        }
      }
    };

    fetchPartsUsingArticleIds();
  }, []);

  const PurchaseHistoryLandingPage = ({
    spareparts,
  }: {
    spareparts: SparePart[];
  }): JSX.Element => {
    if (isLoading) {
      return (
        <>
          <Loading
            text={t('loading', {
              loading: t('spareParts.spareParts'),
            })}
          >
            <LoadingBall />
          </Loading>
        </>
      );
    }

    if (spareparts.length === 0) {
      return (
        <ArticleIdNotFound>
          {t('spareParts.landingPage.error.couldNotFindArticleId')}
        </ArticleIdNotFound>
      );
    }

    return (
      <>
        <SearchLayoutTitle>
          <TMarkUp value={t('spareParts.spareParts')} />
        </SearchLayoutTitle>
        <p>{t('spareParts.landingPage.intro.steppedAteLostFixing')}</p>
        <p>
          {t('spareParts.landingPage.intro.itsOkToNeedSparePart')} <br />
          {t('spareParts.landingPage.intro.consultAssemblyToVerify')}
        </p>
        <InformationWrapper>
          <IconWrapper>
            <SSRIcon paths={InformationCircle} />
          </IconWrapper>
          <p>{t('spareParts.landingPage.intro.beAwareOfUsingWrongPart')}</p>
        </InformationWrapper>
        <StyledDivider />
        <FlexContainer>
          <FlexContent>
            {spareparts.map((articleItem: any, index: number) => {
              return (
                <React.Fragment key={index}>
                  <ProductParts key={index} articleItem={articleItem} />
                  <StyledDivider />
                </React.Fragment>
              );
            })}
            <SearchLandingPage />
          </FlexContent>
          <div>
            <InCart isAutoResize={false} />
          </div>
        </FlexContainer>
      </>
    );
  };

  return (
    <>
      <SiteMetadata pageTitle={title} />
      <Breadcrumbs crumbs={crumbs} />
      {hasArticleIdsQueryInUrl ? (
        <PurchaseHistoryLandingPage spareparts={spareparts} />
      ) : (
        <OrderLayout>
          <Search />
          <InCart isAutoResize={true} />
        </OrderLayout>
      )}
      {!isDesktop && <MobileCart />}
    </>
  );
};
export default translatePage()(Order);
